import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useOutletContext } from "react-router";
import type { AppOutletContext } from "@/src/layouts/AppLayout";
import { useToast } from "@/components/action-feedback";
import { Button } from "@/components/button";
import { ConfirmDialog } from "@/components/confirm-dialog";
import { Icon } from "@/components/icons";
import { SectionHeader } from "../_components/section-header";
import { SectionPage } from "../_components/section-page";
import { CreateTokenModal } from "../_components/create-token-modal";
import { AddWebhookModal } from "../_components/add-webhook-modal";
import {
  listApiTokens,
  deleteApiToken,
  setDefaultApiToken,
  type ApiToken,
} from "@/src/services/api_tokens_service";
import { setAuthToken } from "@/src/services/base_service";
import {
  listWebhooks,
  deleteWebhook as deleteWebhookApi,
  reEnableWebhook,
  pingWebhook,
  type Webhook,
} from "@/src/services/webhook_service";
import { formatDate } from "@/app/(app)/_lib/format";

/** Mask a token by default for display — the backend now sends the full key on
 *  both create and list, so masking here is purely cosmetic; the eye action
 *  reveals it per row. Pads with dots to the same length as the real key (not a
 *  fixed "•••") so toggling reveal doesn't reflow the column width. */
function maskToken(key: string): string {
  return `${key.slice(0, 8)}${"•".repeat(Math.max(key.length - 8, 3))}`;
}

type LoadStatus = "loading" | "error" | "ready";
type Feedback = { tone: "ok" | "error"; text: string };

export default function ApiPage() {
  // `infoResolved` flips true once GET /info/ has settled, which is when the API
  // version is hydrated. The token + webhook list responses are version-dependent
  // (e.g. token `created_at`), so firing before hydration sends no version header
  // and the backend's default response omits those fields. Gate the loads on it.
  const ctx = useOutletContext<AppOutletContext | null>();
  const infoResolved = ctx?.infoResolved ?? false;

  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [tokenStatus, setTokenStatus] = useState<LoadStatus>("loading");
  const [actionError, setActionError] = useState<string | null>(null);
  const showToast = useToast();
  const [revealedKeys, setRevealedKeys] = useState<Set<string>>(new Set());

  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [webhookStatus, setWebhookStatus] = useState<LoadStatus>("loading");
  const [webhookFeedback, setWebhookFeedback] = useState<Feedback | null>(null);

  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [pendingDeleteToken, setPendingDeleteToken] = useState<ApiToken | null>(null);
  const [pendingDeleteWebhook, setPendingDeleteWebhook] = useState<Webhook | null>(null);

  // `silent` skips the "loading" flip for post-mutation refreshes (delete/create/
  // set-default) — the table already holds fresh-enough rows, and briefly
  // swapping it for the 2-row skeleton produces a jarring collapse/re-expand
  // flicker. Only the very first load (from the boot effect below) should show it.
  const loadTokens = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) setTokenStatus("loading");
    setActionError(null);
    const res = await listApiTokens();
    if (!res.ok) {
      setTokenStatus("error");
      return;
    }
    setTokens(res.value.filter((t) => !t.isDeleted));
    setTokenStatus("ready");
  }, []);

  const loadWebhooks = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!opts.silent) setWebhookStatus("loading");
    const res = await listWebhooks();
    if (!res.ok) {
      setWebhookStatus("error");
      return;
    }
    setWebhooks(res.value);
    setWebhookStatus("ready");
  }, []);

  // Wait for /info/ to hydrate the API version before fetching — otherwise the
  // version-dependent list responses come back without created_at / name. Status
  // stays "loading" (skeleton) until then, so there's no flash of em-dashes.
  useEffect(() => {
    if (!infoResolved) return;
    void loadTokens();
  }, [infoResolved, loadTokens]);

  useEffect(() => {
    if (!infoResolved) return;
    void loadWebhooks();
  }, [infoResolved, loadWebhooks]);

  async function deleteToken(key: string) {
    const res = await deleteApiToken(key);
    if (!res.ok) {
      setActionError(res.error.message);
      return;
    }
    await loadTokens({ silent: true });
    showToast({ tone: "success", text: "Token deleted successfully." });
  }

  async function setDefaultToken(key: string) {
    const res = await setDefaultApiToken(key);
    if (!res.ok) {
      setActionError(res.error.message);
      return;
    }
    // The bearer used for every request IS the default token's key (see
    // base_service.ts) — swap it in-memory now, or the previous default keeps
    // authenticating subsequent calls (e.g. deleting it fails: "You can not
    // delete the same token you are using in the request").
    setAuthToken(res.value.key);
    await loadTokens({ silent: true });
    showToast({ tone: "success", text: "Token set as default successfully." });
  }

  function toggleRevealToken(key: string) {
    setRevealedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function copyToken(key: string) {
    await navigator.clipboard.writeText(key);
    showToast({ tone: "success", text: "Token copied to clipboard." });
  }

  async function removeWebhook(id: string) {
    setWebhookFeedback(null);
    const res = await deleteWebhookApi(id);
    if (!res.ok) {
      setWebhookFeedback({ tone: "error", text: res.error.message });
      return;
    }
    await loadWebhooks({ silent: true });
    showToast({ tone: "success", text: "Webhook deleted successfully." });
  }

  async function sendTestEvent(id: string) {
    setWebhookFeedback(null);
    const res = await pingWebhook(id);
    if (!res.ok) {
      setWebhookFeedback({ tone: "error", text: res.error.message });
      return;
    }
    setWebhookFeedback(
      res.value.delivered
        ? { tone: "ok", text: "Test event delivered." }
        : { tone: "error", text: "Test event sent, but the endpoint didn't accept it." },
    );
  }

  async function enableWebhook(id: string) {
    setWebhookFeedback(null);
    const res = await reEnableWebhook(id);
    if (!res.ok) {
      setWebhookFeedback({ tone: "error", text: res.error.message });
      return;
    }
    await loadWebhooks({ silent: true });
    showToast({ tone: "success", text: "Webhook re-enabled successfully." });
  }

  return (
    <SectionPage title="API & Webhooks" width="full">
      <div className="card @container flex flex-col gap-4">
        <SectionHeader
          title="API tokens"
          description="Tokens authenticate requests to the sanctions.io API. Deleting a token immediately blocks any user using it."
          action={
            <Button hierarchy="primary" onClick={() => setTokenModalOpen(true)}>
              Create token
            </Button>
          }
        />

        {actionError && (
          <p
            role="alert"
            className="rounded-[4px] border border-red-500 bg-red-100 px-3 py-2 text-small text-red-600"
          >
            {actionError}
          </p>
        )}

        {tokenStatus === "loading" ? (
          <RowsSkeleton />
        ) : tokenStatus === "error" ? (
          <SectionError message="Couldn’t load API tokens." onRetry={loadTokens} />
        ) : tokens.length === 0 ? (
          <EmptyRow message="No tokens yet. Create one to start using the API." />
        ) : (
          // overflow-x-auto (not -hidden) so the wide table scrolls on narrow viewports instead of clipping columns
          <div className="overflow-x-auto rounded-[4px] border border-grey-200">
            <table className="w-full text-small">
              <thead className="bg-grey-50">
                <tr className="text-left text-grey-900">
                  <Th>Name</Th>
                  <Th>Environment</Th>
                  <Th>Token</Th>
                  <Th>Created at</Th>
                  <Th>Modified at</Th>
                  <Th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => {
                  // The default token can't be deleted or re-defaulted; sandbox
                  // tokens can never be the default. Show/Copy are always available.
                  const items: KebabItem[] = [
                    {
                      label: revealedKeys.has(t.key) ? "Hide token" : "Show token",
                      icon: revealedKeys.has(t.key) ? "visibility_off" : "visibility",
                      onClick: () => toggleRevealToken(t.key),
                    },
                    {
                      label: "Copy token",
                      icon: "content_copy",
                      onClick: () => void copyToken(t.key),
                    },
                    ...(!t.isDefault && !t.isSandbox
                      ? [
                          {
                            label: "Set as default",
                            icon: "key",
                            onClick: () => void setDefaultToken(t.key),
                          },
                        ]
                      : []),
                    ...(!t.isDefault
                      ? [
                          {
                            label: "Delete token",
                            icon: "delete",
                            tone: "danger" as const,
                            onClick: () => setPendingDeleteToken(t),
                          },
                        ]
                      : []),
                  ];
                  return (
                    <tr key={t.key} className="border-t border-grey-200">
                      <Td className="text-black">{t.name || "—"}</Td>
                      <Td>
                        <div className="flex items-center gap-2">
                          <EnvironmentBadge isSandbox={t.isSandbox} />
                          {t.isDefault && <DefaultBadge />}
                        </div>
                      </Td>
                      <Td className="font-mono text-grey-900">
                        {revealedKeys.has(t.key) ? t.key : maskToken(t.key)}
                      </Td>
                      {/* Backend sends a full ISO timestamp (e.g. 2026-06-18T01:40:35.260839Z);
                          formatDate slices it to YYYY-MM-DD (matches the Last login column).
                          Passing the string directly — not new Date() — avoids a timezone day-shift. */}
                      <Td className="whitespace-nowrap text-black">{formatDate(t.createdAt)}</Td>
                      <Td className="whitespace-nowrap text-black">{formatDate(t.timestamp)}</Td>
                      <Td className="text-right">
                        <RowKebab items={items} />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card @container flex flex-col gap-4">
        <SectionHeader
          title="Webhooks"
          description="We send webhook event data to your endpoint when an event happens."
          action={
            <Button hierarchy="primary" onClick={() => setWebhookModalOpen(true)}>
              Create webhook
            </Button>
          }
        />

        {webhookFeedback && (
          <p
            role={webhookFeedback.tone === "error" ? "alert" : "status"}
            className={`rounded-[4px] border px-3 py-2 text-small ${
              webhookFeedback.tone === "error"
                ? "border-red-500 bg-red-100 text-red-600"
                : "border-green-400 bg-green-100 text-green-700"
            }`}
          >
            {webhookFeedback.text}
          </p>
        )}

        {webhookStatus === "loading" ? (
          <RowsSkeleton />
        ) : webhookStatus === "error" ? (
          <SectionError message="Couldn’t load webhooks." onRetry={loadWebhooks} />
        ) : webhooks.length === 0 ? (
          <EmptyRow message="No webhooks yet. Create one to receive event callbacks." />
        ) : (
          // overflow-x-auto (not -hidden) so the wide table scrolls on narrow viewports instead of clipping columns
          <div className="overflow-x-auto rounded-[4px] border border-grey-200">
            <table className="w-full text-small">
              <thead className="bg-grey-50">
                <tr className="text-left text-grey-900">
                  <Th>Name</Th>
                  <Th>Environment</Th>
                  <Th>Endpoint</Th>
                  <Th>Status</Th>
                  <Th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {webhooks.map((w) => {
                  const items: KebabItem[] = [
                    {
                      label: "Send test event",
                      icon: "send",
                      onClick: () => void sendTestEvent(w.id),
                    },
                    ...(w.disabled
                      ? [
                          {
                            label: "Re-enable",
                            icon: "restart_alt",
                            onClick: () => void enableWebhook(w.id),
                          },
                        ]
                      : []),
                    {
                      label: "Delete",
                      icon: "delete",
                      tone: "danger" as const,
                      onClick: () => setPendingDeleteWebhook(w),
                    },
                  ];
                  return (
                    <tr key={w.id} className="border-t border-grey-200">
                      <Td className="text-black">{w.name || "—"}</Td>
                      <Td>
                        <EnvironmentBadge isSandbox={w.isSandbox} />
                      </Td>
                      <Td className="font-mono text-black break-all">{w.url}</Td>
                      <Td>
                        <WebhookStatusBadge disabled={w.disabled} failures={w.failures} />
                      </Td>
                      <Td className="text-right">
                        <RowKebab items={items} />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card @container flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-h4 text-black">API Documentation</h2>
          <p className="text-small text-grey-900">
            Explore our{" "}
            <a
              href="https://api-docs.sanctions.io/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-green-700 underline hover:text-green-600"
            >
              API documentation
            </a>{" "}
            to learn more about available endpoints and how to use them.
          </p>
        </div>
      </div>

      <CreateTokenModal
        open={tokenModalOpen}
        onClose={() => setTokenModalOpen(false)}
        onCreate={() => {
          void loadTokens({ silent: true });
          showToast({ tone: "success", text: "Token created successfully." });
        }}
        insetLeft={ctx?.mainInsetLeft}
      />
      <AddWebhookModal
        open={webhookModalOpen}
        onClose={() => setWebhookModalOpen(false)}
        onCreated={() => {
          void loadWebhooks({ silent: true });
          showToast({ tone: "success", text: "Webhook created successfully." });
        }}
        insetLeft={ctx?.mainInsetLeft}
      />

      <ConfirmDialog
        open={pendingDeleteToken !== null}
        onClose={() => setPendingDeleteToken(null)}
        onConfirm={() => {
          if (pendingDeleteToken) void deleteToken(pendingDeleteToken.key);
          setPendingDeleteToken(null);
        }}
        title="Delete this token?"
        description="Any user using this token will lose access immediately. This can't be undone."
        confirmLabel="Delete token"
        destructive
        insetLeft={ctx?.mainInsetLeft}
      />

      <ConfirmDialog
        open={pendingDeleteWebhook !== null}
        onClose={() => setPendingDeleteWebhook(null)}
        onConfirm={() => {
          if (pendingDeleteWebhook) void removeWebhook(pendingDeleteWebhook.id);
          setPendingDeleteWebhook(null);
        }}
        title="Delete webhook?"
        description={
          pendingDeleteWebhook
            ? `Events will no longer be delivered to ${pendingDeleteWebhook.url}. This can't be undone.`
            : undefined
        }
        confirmLabel="Delete webhook"
        destructive
        insetLeft={ctx?.mainInsetLeft}
      />
    </SectionPage>
  );
}

type KebabItem = {
  label: string;
  icon: string;
  tone?: "default" | "danger";
  onClick: () => void;
};

function RowKebab({ items }: { items: KebabItem[] }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    function position() {
      const node = triggerRef.current;
      if (!node) return;
      const rect = node.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
      });
    }
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handlePointer(e: MouseEvent) {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        className={`inline-flex size-7 items-center justify-center rounded-[4px] text-grey-900 transition-colors hover:bg-grey-100 ${
          open ? "bg-grey-100" : ""
        }`}
      >
        <Icon name="more_vert" size={20} />
      </button>
      {open &&
        coords &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{
              position: "fixed",
              top: coords.top,
              right: coords.right,
            }}
            className="z-50 flex min-w-[180px] flex-col rounded-[4px] border border-grey-200 bg-white p-1 shadow-soft"
          >
            {items.map((item) => {
              const toneClass =
                item.tone === "danger"
                  ? "text-red-600 hover:bg-red-100"
                  : "text-grey-900 hover:bg-grey-100";
              return (
                <button
                  key={item.label}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    item.onClick();
                  }}
                  className={`flex items-center gap-2 rounded-[4px] px-2 py-1.5 text-left text-small font-medium transition-colors ${toneClass}`}
                >
                  <Icon name={item.icon} size={20} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </>
  );
}

function DefaultBadge() {
  return (
    <span
      className="inline-flex items-center rounded-[4px] bg-grey-900 px-2 py-0.5 text-tiny font-medium text-white"
      title="Default token — used by the frontend to call the API"
    >
      Default
    </span>
  );
}

function EnvironmentBadge({ isSandbox }: { isSandbox: boolean }) {
  const label = isSandbox ? "Sandbox" : "Live";
  const cls = isSandbox ? "bg-grey-100 text-grey-900" : "bg-green-200 text-green-900";
  return (
    <span
      className={`inline-flex items-center rounded-[4px] px-2 py-0.5 text-tiny font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

/** Webhook delivery state — backend disables a hook after repeated failures. */
function WebhookStatusBadge({ disabled, failures }: { disabled: boolean; failures: number }) {
  if (disabled) {
    return (
      <span className="inline-flex items-center rounded-[4px] bg-red-100 px-2 py-0.5 text-tiny font-medium text-red-600">
        Disabled{failures > 0 ? ` · ${failures} failures` : ""}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-[4px] bg-green-200 px-2 py-0.5 text-tiny font-medium text-green-900">
      Active
    </span>
  );
}

function RowsSkeleton() {
  return (
    <div
      className="flex flex-col gap-px overflow-hidden rounded-[4px] border border-grey-200"
      aria-busy="true"
    >
      {Array.from({ length: 2 }).map((_, i) => (
        <div key={i} className="h-12 w-full animate-pulse bg-grey-50" />
      ))}
    </div>
  );
}

function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-[4px] border border-grey-200 bg-white px-4 py-8 text-center">
      <div className="flex size-9 items-center justify-center rounded-[4px] bg-red-100 text-red-600">
        <Icon name="error" size={20} />
      </div>
      <p className="text-small text-grey-900">{message}</p>
      <Button
        hierarchy="secondary"
        size="sm"
        leftIcon={<Icon name="refresh" size={20} />}
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

function EmptyRow({ message }: { message: string }) {
  return (
    <div className="rounded-[4px] border border-dashed border-grey-200 px-3 py-6 text-center text-small text-grey-900">
      {message}
    </div>
  );
}

function Th({ children, className = "", ...rest }: React.ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th className={`whitespace-nowrap px-3 py-2 text-small font-medium ${className}`} {...rest}>
      {children}
    </th>
  );
}

function Td({ children, className = "", ...rest }: React.TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={`px-3 py-2 ${className}`} {...rest}>
      {children}
    </td>
  );
}
